import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Send
} from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeLintIssue,
  OutgoingIntakePresendIssue
} from "@/api/types/outgoingIntake";
import { APIError } from "@/api/utils/errors";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { toastEmailFailed } from "../linkToasts";
import { LintBanner } from "./LintBanner";
import {
  errorCount,
  presendChecklist,
  withServerIssues,
  type PresendItem
} from "./presend";
import { cadenceLabel } from "./reminders";
import {
  DEFAULT_EXPIRY_DAYS,
  DEFAULT_REMINDERS,
  type DraftLinkSettings
} from "./useIntakeDraft";
import { firstName } from "./useSubmissionReview";
import { intakeVideoKey, useVideoUpload } from "./welcomeVideo/uploadStore";

type Props = {
  /** The intake to send; null keeps the sheet closed. */
  intake: OutgoingIntake | null;
  /** The live draft's questions when sending from the editor. */
  schema?: OutgoingIntakeFormNode[];
  /** The live draft's delivery settings; otherwise the intake's. */
  delivery?: DraftLinkSettings;
  clientEmail: string | null;
  clientName?: string | null;
  onOpenChange: (open: boolean) => void;
  /** Flush unsaved edits before sending (the intake page's autosave). */
  beforeSend?: () => Promise<void>;
  onSent?: (intake: OutgoingIntake, link: string) => void;
  /** Jump to a question the checklist points at (closes the sheet). */
  onFix?: (nodeId: string) => void;
  /** Replace flagged client-facing text in the draft (the lint's rewrite). */
  onApplyText?: (issue: OutgoingIntakeLintIssue, text: string) => void;
  /** Open the delivery settings (closes the sheet). */
  onEditDelivery?: () => void;
  /** Focus the note to the client (closes the sheet). */
  onEditNote?: () => void;
};

const CheckIcon: FC<{ severity: PresendItem["severity"] }> = ({ severity }) =>
  severity === "ok" ? (
    <CheckCircle2 className="size-3.5 shrink-0 text-success" />
  ) : severity === "warning" ? (
    <AlertTriangle className="size-3.5 shrink-0 text-warn" />
  ) : (
    <AlertCircle className="size-3.5 shrink-0 text-danger" />
  );

const issuesOf = (e: unknown): OutgoingIntakePresendIssue[] | null => {
  if (!(e instanceof APIError)) return null;
  const details = e.details as
    | { code?: string; issues?: OutgoingIntakePresendIssue[] }
    | undefined;
  return details?.code === "presend_failed" && Array.isArray(details.issues)
    ? details.issues
    : null;
};

const deliveryOf = (
  intake: OutgoingIntake | null,
  clientEmail: string | null
): DraftLinkSettings => ({
  recipient_email: intake?.recipient_email || clientEmail || "",
  attorney_note: intake?.attorney_note ?? "",
  expires_in_days: intake?.expires_in_days ?? DEFAULT_EXPIRY_DAYS,
  reminder_schedule:
    intake?.reminder_schedule === undefined
      ? DEFAULT_REMINDERS
      : intake.reminder_schedule
});

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex min-h-7 items-baseline gap-3 text-base">
    <span className="w-24 shrink-0 text-sm text-fg-3">{label}</span>
    <span className="min-w-0 flex-1 truncate text-fg">{value}</span>
  </div>
);

/**
 * "Send…": who it goes to, a summary of the delivery settings, the pre-send
 * checks and the wording lint. Settings are edited on the page, not here.
 */
export const SendSheet: FC<Props> = ({
  intake,
  schema,
  delivery,
  clientEmail,
  clientName,
  onOpenChange,
  beforeSend,
  onSent,
  onFix,
  onApplyText,
  onEditDelivery,
  onEditNote
}) => {
  const { clientId, invalidate } = useRequestsIde();
  const data = useRequestsData();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [serverIssues, setServerIssues] = useState<
    OutgoingIntakePresendIssue[]
  >([]);
  const who = firstName(clientName) ?? "the client";
  const intakeId = intake?.outgoing_intake_id ?? null;
  const settings = delivery ?? deliveryOf(intake, clientEmail);
  const upload = useVideoUpload(intakeId ? intakeVideoKey(intakeId) : null);

  useEffect(() => {
    if (!intake) return;
    const initial = delivery ?? deliveryOf(intake, clientEmail);
    setEmail(initial.recipient_email || clientEmail || "");
    setNote(initial.attorney_note);
    setServerIssues([]);
    setWaiting(false);
    // Once per opening; edits after that are the sheet's own.
  }, [intakeId, clientEmail]);

  const questions = schema ?? intake?.schema ?? [];
  const items = useMemo(
    () => withServerIssues(presendChecklist(questions, email), serverIssues),
    [email, questions, serverIssues]
  );

  // Client-visible text that reads as internal, checked once per opening
  // against what's on screen. Each finding is rewritten, edited or waved
  // through before sending; a failed check doesn't block.
  const [lintStatus, setLintStatus] = useState<"checking" | "done" | "failed">(
    "done"
  );
  const [lintIssues, setLintIssues] = useState<OutgoingIntakeLintIssue[]>([]);
  const [lintHandled, setLintHandled] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  useEffect(() => {
    if (!intakeId) return undefined;
    let cancelled = false;
    setLintStatus("checking");
    setLintIssues([]);
    setLintHandled(new Set());
    OutgoingIntakesAPI.lint(clientId, intakeId, {
      schema: questions,
      attorney_note: settings.attorney_note || null
    })
      .then((res) => {
        if (cancelled) return;
        setLintIssues(res.issues ?? []);
        setLintStatus("done");
      })
      .catch(() => {
        if (!cancelled) setLintStatus("failed");
      });
    return () => {
      cancelled = true;
    };
    // Once per opening: rewrites applied from the banner change `questions`.
  }, [clientId, intakeId]);
  const openLint = lintIssues.filter((i) => !lintHandled.has(i.key));
  const handleLint = (issue: OutgoingIntakeLintIssue) =>
    setLintHandled((prev) => new Set(prev).add(issue.key));
  const errors = errorCount(items) + openLint.length;
  const blocked = errors > 0 || lintStatus === "checking";

  const send = async () => {
    if (!intake) return;
    setSending(true);
    try {
      await beforeSend?.();
      const res = await OutgoingIntakesAPI.send(
        clientId,
        intake.outgoing_intake_id,
        {
          recipient_email: email.trim() || undefined,
          expires_in_days: settings.expires_in_days,
          attorney_note: note.trim() || undefined,
          reminder_schedule: settings.reminder_schedule
        }
      );
      data.applyIntake(res.outgoing_intake);
      invalidate(["intakes", "activity"]);
      if (res.email_delivered === false) {
        toastEmailFailed(clientId, intake.outgoing_intake_id);
      } else {
        toast.success(`Sent to ${email.trim()}`, {
          action: {
            label: "Copy link",
            onClick: () => void navigator.clipboard.writeText(res.public_link)
          }
        });
      }
      onSent?.(res.outgoing_intake, res.public_link);
      onOpenChange(false);
    } catch (e) {
      const issues = issuesOf(e);
      if (issues) setServerIssues(issues);
      else
        toast.error(
          e instanceof Error ? e.message : "Couldn't send the intake"
        );
    } finally {
      setSending(false);
    }
  };

  // Send waits for the welcome video; it goes as soon as the upload ends.
  useEffect(() => {
    if (!waiting || upload) return;
    setWaiting(false);
    void send();
  }, [waiting, upload]);

  const close = () => {
    setWaiting(false);
    onOpenChange(false);
  };
  const resend = intake ? intake.status !== "draft" : false;
  const video = upload
    ? "Uploading…"
    : intake?.welcome_video_url
    ? "Added"
    : "None";
  const sendLabel = resend
    ? "Send again"
    : who === "the client"
    ? "Send"
    : `Send to ${who}`;

  return (
    <Sheet
      open={intake !== null}
      onOpenChange={(o) => (o ? onOpenChange(o) : close())}
    >
      <SheetContent
        className="flex w-110 flex-col gap-0 data-[side=right]:sm:max-w-110"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>
            {resend ? "Send again" : "Send"} “{intake?.title}”
          </SheetTitle>
          <SheetDescription>
            {resend
              ? `${
                  who === "the client" ? "The client gets" : `${who} gets`
                } a fresh link; the old one stops working. To nudge with the same link, use Remind.`
              : `${
                  who === "the client" ? "The client gets" : `${who} gets`
                } an email with a link to fill in the intake. You'll see when it's delivered and opened.`}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          <Field>
            <FieldLabel htmlFor="send-email" className="text-sm">
              Send To
            </FieldLabel>
            <Input
              id="send-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {clientEmail && email.trim() === clientEmail ? (
              <FieldDescription className="text-sm">
                From the client's profile.
              </FieldDescription>
            ) : null}
          </Field>

          <div
            className="flex flex-col rounded-lg bg-fill-1 px-3 py-2"
            data-testid="send-delivery"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-fg-2">Delivery</span>
              {onEditDelivery ? (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    close();
                    onEditDelivery();
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </div>
            <SummaryRow
              label="Link Expires"
              value={`After ${settings.expires_in_days} days`}
            />
            <SummaryRow
              label="Reminders"
              value={cadenceLabel(settings.reminder_schedule)}
            />
            <SummaryRow label="Welcome Video" value={video} />
            <SummaryRow label="Note" value={note.trim() || "None"} />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-fg-2">
                Before You Send
              </span>
              {errors ? (
                <span className="text-sm text-danger">{errors} to fix</span>
              ) : (
                <span className="text-sm text-success">Ready</span>
              )}
            </div>
            {lintStatus !== "failed" ? (
              <LintBanner
                status={lintStatus}
                issues={openLint}
                schema={questions}
                who={who}
                onRewrite={(issue) => {
                  const text = issue.suggestion ?? "";
                  if (issue.field === "note") setNote(text);
                  onApplyText?.(issue, text);
                  handleLint(issue);
                }}
                onEdit={(issue) => {
                  if (issue.field === "note") {
                    handleLint(issue);
                    if (onEditNote) {
                      close();
                      onEditNote();
                    }
                  } else if (issue.node_id && onFix) {
                    onFix(issue.node_id);
                    close();
                  }
                }}
                onDismiss={handleLint}
                canChange={(issue) =>
                  issue.field === "note" ||
                  (Boolean(onApplyText) && Boolean(onFix))
                }
              />
            ) : null}
            {items.map((c) => (
              <div
                key={`${c.code}|${c.nodeId ?? ""}|${c.message}`}
                className="flex min-h-7 items-center gap-2 text-base"
              >
                <CheckIcon severity={c.severity} />
                <span
                  className={cn(
                    "min-w-0 flex-1",
                    c.severity === "ok" ? "text-fg-3" : "text-fg"
                  )}
                >
                  {c.message}
                </span>
                {c.fix && c.nodeId && onFix ? (
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() => {
                      onFix(c.nodeId as string);
                      close();
                    }}
                  >
                    {c.fix}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        {waiting && upload ? (
          <div
            className="flex flex-col gap-2 border-t border-line-1 px-4 py-3"
            data-testid="send-waiting"
          >
            <span className="text-base text-fg">
              Sends when the welcome video finishes uploading (
              {Math.round(upload.progress * 100)}%).
            </span>
            <Progress
              value={Math.round(upload.progress * 100)}
              aria-label="Upload progress"
            />
            <Button
              variant="ghost"
              size="xs"
              className="self-start"
              onClick={upload.cancel}
            >
              Send Without the Video
            </Button>
          </div>
        ) : null}
        <SheetFooter className="flex-row items-center gap-2 border-t border-line-1">
          {errors ? (
            <span className="mr-auto text-sm whitespace-nowrap text-fg-3">
              Fix {errors} {errors === 1 ? "item" : "items"} to send
            </span>
          ) : lintStatus === "checking" ? (
            <span className="mr-auto text-sm whitespace-nowrap text-fg-3">
              Checking the wording…
            </span>
          ) : (
            <span className="mr-auto" />
          )}
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sending || waiting || blocked}
            onClick={() => (upload ? setWaiting(true) : void send())}
          >
            {sending || waiting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
            {sending ? "Sending…" : waiting ? "Waiting for Video…" : sendLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
